import { useDispatch } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { showMessage } from 'dashboard/redux/actions/messages';
import { FieldValue } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiModel, ApiSingletonPayload } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

export interface CreateInquiryProps {
  templateId: string;
  themeSetId?: string;
  referenceId?: string;
  accountTypeId?: string;
  fields?: Record<string, FieldValue>;
}

export interface CreateLegacyInquiryProps {
  templateId: string;
  themeId?: string;
  referenceId?: string;
  accountTypeId?: string;
}

export interface CreateInquiryForAccountProps {
  accountId: string;
  templateId: string;
  themeSetId?: string;
  fields?: Record<string, FieldValue>;
}

export interface CreateLegacyInquiryForAccountProps {
  accountId: string;
  templateId: string;
  themeId?: string;
}

export type UseCreateInquiryProps =
  | CreateInquiryForAccountProps
  | CreateInquiryProps
  | CreateLegacyInquiryProps
  | CreateLegacyInquiryForAccountProps;

export default ({ environment }: { environment: Environment }) => {
  const dispatch = useDispatch();

  const { error: handleCreateInquiryError, callback: handleCreateInquiry } = useApiCallback(
    async (props: UseCreateInquiryProps) => {
      const inquiryResponse = await SessionClientDashboard.postResourceCreate<
        Inquiry,
        | ApiSingletonPayload<
            {
              'inquiry-template-id': string;
              'environment-id': string;
              'account-id'?: string | undefined;
              'theme-set-id'?: string | undefined;
              'reference-id'?: string | undefined;
              'account-type-id'?: string | undefined;
              fields?: Record<string, FieldValue>;
            },
            ApiModel.Inquiry
          >
        | ApiSingletonPayload<
            {
              'template-id': string;
              'environment-id': string;
              'theme-id'?: string | undefined;
              'reference-id'?: string | undefined;
              'account-type-id'?: string | undefined;
            },
            ApiModel.Inquiry
          >
      >(
        props.templateId.startsWith('itmpl')
          ? {
              attributes: {
                'inquiry-template-id': props.templateId,
                'theme-set-id': 'themeSetId' in props ? props.themeSetId : undefined,
                'environment-id': environment.id,
                'account-id': 'accountId' in props ? props.accountId : undefined,
                'reference-id': 'referenceId' in props ? props.referenceId : undefined,
                'account-type-id': 'accountTypeId' in props ? props.accountTypeId : undefined,
                fields: 'fields' in props ? props.fields : {},
              },
              type: ApiModel.Inquiry as const,
            }
          : {
              attributes: {
                'template-id': props.templateId,
                'theme-id': 'themeId' in props ? props.themeId : undefined,
                'environment-id': environment.id,
                'account-id': 'accountId' in props ? props.accountId : undefined,
                'reference-id': 'referenceId' in props ? props.referenceId : undefined,
                'account-type-id': 'accountTypeId' in props ? props.accountTypeId : undefined,
              },
              type: ApiModel.Inquiry as const,
            },
      );

      dispatch(showMessage(`Successfully created inquiry`, MessageType.Success));
      return inquiryResponse.data;
    },
    [],
  );

  useApiErrorHandler({
    error: handleCreateInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
      } else if (
        error.status === 409 &&
        error.errors?.[0].code === 'account_reference_id_not_unique_error'
      ) {
        dispatch(
          showMessage(
            'Account of a different type already exists with this reference ID.',
            MessageType.Failure,
          ),
        );
      } else if (error.status === 409 && error.errors?.[0].code === 'account_archived_error') {
        dispatch(
          showMessage('The account with this reference ID is archived.', MessageType.Failure),
        );
      } else if (error.status === 429 && error.errors?.[0].code === 'quota_exceeded_error') {
        dispatch(showMessage(error.errors?.[0].details as string, MessageType.Failure));
      } else {
        dispatch(showMessage('Failed to create inquiry', MessageType.Failure));
      }
    },
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  return {
    handleCreateInquiry,
  };
};
